import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { BrainCircuitIcon, CheckCircle2Icon, XCircleIcon, TimerIcon, TrophyIcon } from "lucide-react";

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const questions: Question[] = [
  {
    id: 1,
    question: "What is compound interest?",
    options: [
      "Interest earned only on the principal amount",
      "Interest earned on both principal and accumulated interest",
      "A fixed interest rate that never changes",
      "Interest paid at the end of a loan term"
    ],
    correctAnswer: 1,
    explanation: "Compound interest is interest earned not only on your initial principal but also on the accumulated interest from previous periods. This creates a snowball effect that accelerates wealth growth over time."
  },
  {
    id: 2,
    question: "Which investment typically carries the highest risk?",
    options: [
      "Government bonds",
      "Certificates of deposit",
      "Individual stocks",
      "Savings accounts"
    ],
    correctAnswer: 2,
    explanation: "Individual stocks typically carry higher risk than other options listed because their value can be volatile and is directly tied to the performance of a single company."
  },
  {
    id: 3,
    question: "What is diversification in investing?",
    options: [
      "Putting all money in one successful company",
      "Spreading investments across different assets",
      "Only investing in stocks",
      "Keeping all money in a savings account"
    ],
    correctAnswer: 1,
    explanation: "Diversification means spreading your investments across different assets, sectors, or geographic regions to reduce risk. It's the investment equivalent of not putting all your eggs in one basket."
  },
  {
    id: 4,
    question: "What is a good first step in creating a budget?",
    options: [
      "Taking out a loan",
      "Buying stocks",
      "Tracking current spending",
      "Opening a new credit card"
    ],
    correctAnswer: 2,
    explanation: "Tracking your current spending is essential to understand your financial habits and create an effective budget. You need to know where your money is going before you can make meaningful changes."
  },
  {
    id: 5,
    question: "What is the 50/30/20 budgeting rule?",
    options: [
      "50% savings, 30% needs, 20% wants",
      "50% needs, 30% wants, 20% savings",
      "50% wants, 30% savings, 20% needs",
      "50% investments, 30% savings, 20% spending"
    ],
    correctAnswer: 1,
    explanation: "The 50/30/20 rule suggests allocating 50% of your income to needs (essential expenses), 30% to wants (non-essential), and 20% to savings and debt repayment. It's a simple framework for basic budgeting."
  }
];

export const FinancialPuzzleGame = () => {
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameOver, setGameOver] = useState(false);
  const [showCorrectModal, setShowCorrectModal] = useState(false);
  const [showIncorrectModal, setShowIncorrectModal] = useState(false);

  useEffect(() => {
    if (!gameOver && !showExplanation && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeft, showExplanation, gameOver]);

  // Effect for auto-advancing after correct answer
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showCorrectModal) {
      timeout = setTimeout(() => {
        setShowCorrectModal(false);
        handleNextQuestion();
      }, 2000); // Show correct answer for 2 seconds before auto-advancing
    }
    return () => clearTimeout(timeout);
  }, [showCorrectModal]);

  const handleTimeout = () => {
    if (!showExplanation) {
      setShowExplanation(true);
      setShowIncorrectModal(true);
    }
  };

  const handleAnswerSelect = (answerIndex: number) => {
    if (showExplanation || selectedAnswer !== null) return;
    
    setSelectedAnswer(answerIndex);
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answerIndex === currentQuestion.correctAnswer;
    
    if (isCorrect) {
      setScore((prev) => prev + 100);
      setShowCorrectModal(true);
    } else {
      setShowIncorrectModal(true);
      setShowExplanation(true);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setShowExplanation(false);
      setSelectedAnswer(null);
      setTimeLeft(30);
      setShowIncorrectModal(false);
    } else {
      setGameOver(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setShowExplanation(false);
    setSelectedAnswer(null);
    setTimeLeft(30);
    setGameOver(false);
    setShowCorrectModal(false);
    setShowIncorrectModal(false);
  };

  const currentQuestion = questions[currentQuestionIndex];

  if (gameOver) {
    // ... keep existing code (game over screen)
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center justify-center gap-2">
            <TrophyIcon className="w-8 h-8 text-yellow-500 animate-bounce" />
            Game Complete!
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-2xl font-bold">Final Score: {score}</p>
          <p className="text-muted-foreground">
            {score >= 400 ? "Amazing job! You're a financial wizard! 🌟" :
             score >= 300 ? "Great work! Keep learning! 👏" :
             "Good effort! Try again to improve your score! 💪"}
          </p>
        </CardContent>
        <CardFooter className="justify-center">
          <Button onClick={handleRestart} className="animate-pulse">
            Play Again
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <>
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BrainCircuitIcon className="w-6 h-6 text-primary animate-pulse" />
              Financial Puzzle #{currentQuestionIndex + 1}
            </CardTitle>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2">
                <TimerIcon className="w-5 h-5 text-muted-foreground" />
                {timeLeft}s
              </span>
              <span className="font-bold text-primary">Score: {score}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-lg font-medium">{currentQuestion.question}</p>
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <Button
                key={index}
                variant={selectedAnswer === null ? "outline" : 
                       selectedAnswer === index ? 
                         (index === currentQuestion.correctAnswer ? "default" : "destructive") :
                       index === currentQuestion.correctAnswer && showExplanation ? "default" : "outline"}
                className={`w-full justify-start h-auto p-4 text-left ${
                  selectedAnswer !== null ? "cursor-default" : ""
                }`}
                onClick={() => handleAnswerSelect(index)}
                disabled={showExplanation || selectedAnswer !== null}
              >
                <div className="flex items-center gap-2">
                  {selectedAnswer !== null && index === currentQuestion.correctAnswer && (
                    <CheckCircle2Icon className="w-5 h-5 text-green-500" />
                  )}
                  {selectedAnswer === index && index !== currentQuestion.correctAnswer && (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                  {option}
                </div>
              </Button>
            ))}
          </div>
        </CardContent>
        <CardFooter className="justify-end">
          {showExplanation && (
            <Button onClick={handleNextQuestion} className="animate-pulse">
              {currentQuestionIndex < questions.length - 1 ? "Next Question" : "Finish Game"}
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Correct Answer Modal */}
      {showCorrectModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-background rounded-lg p-8 max-w-md w-full mx-4 space-y-4">
            <div className="flex justify-center">
              <CheckCircle2Icon className="w-16 h-16 text-green-500 animate-bounce" />
            </div>
            <p className="text-center text-lg font-medium">Correct!</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">{currentQuestion.explanation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Incorrect Answer Modal */}
      {showIncorrectModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-background rounded-lg p-8 max-w-md w-full mx-4 space-y-4">
            <div className="flex justify-center">
              <XCircleIcon className="w-16 h-16 text-red-500 animate-bounce" />
            </div>
            <p className="text-center text-lg font-medium">Incorrect</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">{currentQuestion.explanation}</p>
            </div>
            <div className="flex justify-center">
              <Button onClick={handleNextQuestion}>
                {currentQuestionIndex < questions.length - 1 ? "Next Question" : "Finish Game"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};